import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import { type BuildKindType } from '@common/types/common';
import { type RequirementsItemProps } from '@common/types/components';
import { FactoryUpgradeInfo } from '@components/Materials/FactoryUpgradeInfo/FactoryUpgradeInfo';
import { RequirementsItem } from '@components/RequirementsItem/RequirementsItem';
import { QuarryUpgradeInfo } from '@components/Resources/QuarryUpgradeInfo/QuarryUpgradeInfo';
import { ResourceTitle } from '@components/ResourceTitle/ResourceTitle';
import { H2, H3 } from '@components/Typography/Typography';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { MainButton } from '@components/ui/Button/Button';
import { Level } from '@components/ui/Level/Level';
import { WorkshopUpgradeInfo } from '@components/Workshop/WorkshopUpgradeInfo/WorkshopUpgradeInfo';

import css from './styles.module.scss';

interface UpgradeBuildProps {
  kind: BuildKindType;
  name: string;
  id: string;
  level: number;
  isComplete: boolean;
  onUpgrade: () => void;
  isLoading?: boolean;
  requirementsList: RequirementsItemProps[];
}

const UpgradeBuild: FC<UpgradeBuildProps> = memo(
  ({
    kind,
    name,
    id,
    level,
    isComplete,
    onUpgrade,
    isLoading,
    requirementsList,
  }) => {
    const { t } = useTranslation();

    const background = getImage({ name: name, field: 'background' });
    const title = getText({ name: name, kind: 'build', t });

    return (
      <main className="h-full w-full">
        <PageBackground
          className="absolute top-0 left-0 h-[41.04vh] z-[-1] dark-gradient"
          backgroundImage={{
            png: background?.img ?? '',
            webp: background?.webp ?? '',
          }}
        />

        <div className={css.buildContent}>
          <ResourceTitle className="mb-4">{title}</ResourceTitle>
          <div className="w-full flex flex-col mt-auto">
            <div className="w-full flex flex-col items-center gap-2">
              <Level value={level} variant="default" />
              <H2 weight="bold">{`${t('Upgrade to level')} ${level + 1}`}</H2>
            </div>
            {kind === 'quarry' && <QuarryUpgradeInfo id={id} withNext={true} />}
            {kind === 'factory' && <FactoryUpgradeInfo id={id} />}
            {kind === 'workshop' && <WorkshopUpgradeInfo id={id} />}
            <div className="w-full flex flex-col items-center mb-4">
              <H3 weight="bold" className="text-white mb-4">
                {t('Requirements')}
              </H3>
              <div className="w-full flex flex-col gap-1">
                {requirementsList?.map((props, index) => (
                  <RequirementsItem key={index} {...props} />
                ))}
              </div>
            </div>
            <MainButton
              isLoading={isLoading}
              onClick={onUpgrade}
              disabled={!isComplete || isLoading}
              inactive={!isComplete}
              text={t('Upgrade')}
            />
          </div>
        </div>
      </main>
    );
  },
);

export { UpgradeBuild };
